import {
	BrainIcon,
	Globe2Icon,
	SparklesIcon,
	WorkflowIcon,
	WrenchIcon,
} from "lucide-react";
import {
	type CSSProperties,
	Fragment,
	memo,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	ChainOfThought,
	ChainOfThoughtContent,
	ChainOfThoughtHeader,
	ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import {
	Conversation,
	ConversationContent,
	ConversationEmptyState,
	ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
	InlineCitation,
	InlineCitationCard,
	InlineCitationCardBody,
	InlineCitationCardTrigger,
	InlineCitationCarousel,
	InlineCitationCarouselContent,
	InlineCitationCarouselHeader,
	InlineCitationCarouselIndex,
	InlineCitationCarouselItem,
	InlineCitationCarouselNext,
	InlineCitationCarouselPrev,
	InlineCitationSource,
	InlineCitationText,
} from "@/components/ai-elements/inline-citation";
import {
	Message,
	MessageContent,
	MessageResponse,
} from "@/components/ai-elements/message";
import {
	Plan,
	PlanAction,
	PlanContent,
	PlanDescription,
	PlanHeader,
	PlanTitle,
	PlanTrigger,
} from "@/components/ai-elements/plan";
import { Shimmer } from "@/components/ai-elements/shimmer";
import {
	Source,
	Sources,
	SourcesContent,
	SourcesTrigger,
} from "@/components/ai-elements/sources";
import {
	Task,
	TaskContent,
	TaskItem,
	TaskItemFile,
	TaskTrigger,
} from "@/components/ai-elements/task";
import {
	Tool,
	type ToolCallState,
	ToolContent,
	ToolHeader,
	ToolInput,
	ToolOutput,
} from "@/components/ai-elements/tool";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMessageTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import {
	type ContextCompactionUiState,
	contextCompactionMarkers,
} from "../../../../email-assistant/core/contextCompaction";
import type { InteractionAnswer } from "../../../../email-assistant/core/toolInteractions";
import {
	type TurnContinuationUiState,
	turnContinuationMarkers,
} from "../../../../email-assistant/core/turnContinuations";
import { splitMentionTokens } from "../../../../email-assistant/core/workflowMentions";
import { orderActivitySteps } from "../../modules/threads/activityOrdering";
import {
	type ContentPart,
	isSource,
	isText,
} from "../../modules/threads/contentPart";
import {
	buildToolResultMap,
	type ChatMessageLike,
	eoirCheckPartFor,
	groupMessagesByTurn,
	interactionPartFor,
	planPartFor,
	runOutcomeFromError,
	scheduledMeetingPartFor,
	type TurnGroup,
	toolResultError,
	unwrapToolResult,
} from "../../modules/threads/messageHelpers";
import { AskQuestionCard } from "./AskQuestionCard";
import { ContextCompactionMarker } from "./ContextCompactionMarker";
import { EoirCheckCard } from "./EoirCheckCard";
import { ResolvedInteractionCard } from "./ResolvedInteractionCard";
import { type RunOutcome, RunOutcomeCard } from "./RunOutcomeCard";
import { ScheduledMeetingCard } from "./ScheduledMeetingCard";
import { TurnContinuationMarker } from "./TurnContinuationMarker";

type CitationSource = {
	url: string;
	title?: string;
	description?: string;
};

type ChatMessage = ChatMessageLike;
type TurnGroupT = TurnGroup<ChatMessage>;

// pure helpers

/**
 * Fallback when the provider didn't emit AI-SDK `source` parts but cited URLs in
 * markdown — e.g. OpenRouter `:online` returns hits as system-context, and Claude
 * emits inline `[label](url)` links instead of typed source parts.
 */
function harvestUrlsFromText(text: string): CitationSource[] {
	const out: CitationSource[] = [];
	const seen = new Set<string>();
	// drop unparsable matches — placeholders like https://... crash URL()
	const parsableHostname = (url: string): string | null => {
		try {
			return new URL(url).hostname;
		} catch {
			return null;
		}
	};
	const mdLink = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
	let m: RegExpExecArray | null = mdLink.exec(text);
	while (m !== null) {
		const url = m[2];
		if (!seen.has(url)) {
			seen.add(url);
			if (parsableHostname(url) !== null) out.push({ url, title: m[1] });
		}
		m = mdLink.exec(text);
	}
	const bare = /(?<![\](])https?:\/\/[^\s)\]]+/g;
	let b: RegExpExecArray | null = bare.exec(text);
	while (b !== null) {
		const url = b[0].replace(/[.,;:]+$/, "");
		if (!seen.has(url)) {
			seen.add(url);
			const hostname = parsableHostname(url);
			if (hostname !== null) out.push({ url, title: hostname });
		}
		b = bare.exec(text);
	}
	return out;
}

function extractSources(parts: ContentPart[]): CitationSource[] {
	const out: CitationSource[] = [];
	const seen = new Set<string>();
	for (const part of parts) {
		if (isSource(part)) {
			const url = part.url;
			if (typeof url !== "string" || url.length === 0) continue;
			if (seen.has(url)) continue;
			seen.add(url);
			out.push({
				url,
				title: part.title,
				description: part.description,
			});
		}
	}
	if (out.length === 0) {
		const fullText = parts
			.filter(isText)
			.map((p) => p.text)
			.join("\n");
		for (const s of harvestUrlsFromText(fullText)) {
			if (!seen.has(s.url)) {
				seen.add(s.url);
				out.push(s);
			}
		}
	}
	return out;
}

function toolStateFor(
	toolCallId: string,
	results: Map<string, unknown>,
): ToolCallState {
	if (!results.has(toolCallId)) return "running";
	return toolResultError(results.get(toolCallId)) ? "error" : "done";
}

/** Strip markdown emphasis so plan titles render cleanly in PlanContent (plain text, not Streamdown). */
function stripMarkdown(text: string): string {
	return text
		.replace(/\*\*(.+?)\*\*/g, "$1")
		.replace(/`([^`]+)`/g, "$1")
		.replace(/\*(.+?)\*/g, "$1")
		.replace(/_(.+?)_/g, "$1")
		.trim();
}

function previewOutput(output: unknown): string | null {
	const value = unwrapToolResult(output);
	if (value !== output) return previewOutput(value);
	if (output === undefined || output === null) return null;
	if (typeof output === "string") {
		const t = output.trim();
		return t.length > 80 ? `${t.slice(0, 80)}…` : t;
	}
	if (Array.isArray(output)) {
		return `${output.length} item${output.length === 1 ? "" : "s"}`;
	}
	if (typeof output === "object") {
		const keys = Object.keys(output as Record<string, unknown>);
		if (keys.length === 0) return null;
		const obj = output as Record<string, unknown>;
		if (typeof obj.summary === "string")
			return String(obj.summary).slice(0, 120);
		if (typeof obj.text === "string") return String(obj.text).slice(0, 120);
		if (Array.isArray(obj.results))
			return `${(obj.results as unknown[]).length} results`;
		return keys.slice(0, 3).join(", ");
	}
	return String(output);
}

function extractFiles(output: unknown): string[] {
	const value = unwrapToolResult(output);
	if (value !== output) return extractFiles(value);
	if (!output || typeof output !== "object") return [];
	const out = output as Record<string, unknown>;
	const collect = (raw: unknown): string[] => {
		if (!Array.isArray(raw)) return [];
		const names: string[] = [];
		for (const item of raw) {
			if (typeof item === "string") names.push(item);
			else if (item && typeof item === "object") {
				const o = item as Record<string, unknown>;
				const name =
					(o.name as string | undefined) ??
					(o.fileName as string | undefined) ??
					(o.title as string | undefined) ??
					(o.path as string | undefined);
				if (typeof name === "string") names.push(name);
			}
		}
		return names;
	};
	return [
		...collect(out.files),
		...collect(out.attachments),
		...collect(out.results),
	].slice(0, 5);
}

// presentational components

/** Loading placeholder — initial fetch and infinite-scroll prepend. */
function MessageSkeleton() {
	return (
		<div className="flex flex-col gap-3" aria-hidden>
			<Skeleton className="h-3 w-24" />
			<Skeleton className="h-24 w-full" />
			<Skeleton className="h-3 w-3/4" />
		</div>
	);
}

export function AssistantBody({
	parts,
	text,
	toolResults,
	streaming,
	pending,
	isLatestTurn,
	activeToolName,
	onAnswer,
	answerInFlight,
	interactionResponses,
	chainOnly,
	afterSteps,
}: {
	parts: ContentPart[];
	text: string;
	toolResults: Map<string, unknown>;
	streaming: boolean;
	pending: boolean;
	isLatestTurn: boolean;
	activeToolName?: string | null;
	onAnswer?: (answerMessage: string) => void;
	answerInFlight?: boolean;
	/** Structured answers to interactive tool calls, keyed by toolCallId. */
	interactionResponses?: Map<string, InteractionAnswer[]>;
	/**
	 * Render only reasoning + tool-call chain (no text/sources/plan/cards). Used
	 * for workflow step activity, whose "answer" is structured JSON, not prose.
	 */
	chainOnly?: boolean;
	afterSteps?: React.ReactNode;
}) {
	const inFlight = streaming || pending;
	const { steps: orderedSteps, allToolCallCount } = orderActivitySteps(
		parts,
		activeToolName,
		inFlight,
		!chainOnly && isLatestTurn && inFlight,
	);
	const sources = extractSources(parts);
	const stepCount = orderedSteps.length;
	const hasChainOfThought = stepCount > 0;
	const showShimmer =
		inFlight &&
		text.length === 0 &&
		allToolCallCount === 0 &&
		!hasChainOfThought;
	const plan = planPartFor(parts, toolResults);
	const interaction = interactionPartFor(parts);
	const scheduledMeeting = scheduledMeetingPartFor(parts, toolResults);
	const eoirCheck = eoirCheckPartFor(parts, toolResults);

	// Defer building heavy per-step JSX until the chain is actually opened (saves
	// hundreds of createElement calls per turn). `hasOpenedChain` stays true after
	// first open so re-collapsing is CSS-only, not a remount.
	const [chainOpen, setChainOpen] = useState(inFlight);
	const [hasOpenedChain, setHasOpenedChain] = useState(inFlight);
	const handleChainOpenChange = useCallback((next: boolean) => {
		setChainOpen(next);
		if (next) setHasOpenedChain(true);
	}, []);

	const chainEl = hasChainOfThought ? (
		<ChainOfThought open={chainOpen} onOpenChange={handleChainOpenChange}>
			<ChainOfThoughtHeader>
				{inFlight ? "Thinking…" : `Steps · ${stepCount}`}
			</ChainOfThoughtHeader>
			<ChainOfThoughtContent>
				{hasOpenedChain || inFlight
					? orderedSteps.map((step, i) => {
							if (step.kind === "reasoning") {
								return (
									<ChainOfThoughtStep
										// biome-ignore lint/suspicious/noArrayIndexKey: reasoning parts are positional in the message
										key={`r${i}`}
										icon={BrainIcon}
										label={`Step ${i + 1} · Reasoning`}
										status="complete"
									>
										<p className="whitespace-pre-wrap text-xs text-muted-foreground">
											{step.text}
										</p>
									</ChainOfThoughtStep>
								);
							}
							if (step.kind === "webSearch") {
								const active = step.sourceCount === 0;
								return (
									<ChainOfThoughtStep
										key="web-search"
										icon={Globe2Icon}
										label={`Step ${i + 1} · ${active ? "Researching" : "Web search"}`}
										description={
											active ? (
												<Shimmer duration={1.4}>
													Checking tools and current sources…
												</Shimmer>
											) : (
												"Complete"
											)
										}
										status={active ? "active" : "complete"}
										role="status"
										aria-live="polite"
										aria-atomic="true"
									/>
								);
							}
							if (step.kind === "activeTool") {
								return (
									<ChainOfThoughtStep
										key="active-tool"
										icon={WrenchIcon}
										label={
											<span className="font-mono text-xs">
												Step {i + 1} · {step.name}
											</span>
										}
										description="Executing…"
										status="active"
									/>
								);
							}
							const tc = step.call;
							const state = toolStateFor(tc.toolCallId, toolResults);
							const result = toolResults.get(tc.toolCallId);
							const errorText = toolResultError(result);
							const files = extractFiles(result);
							const preview = errorText ?? previewOutput(result);
							return (
								<ChainOfThoughtStep
									key={tc.toolCallId}
									icon={WrenchIcon}
									label={
										<span className="flex items-center gap-2 font-mono text-xs">
											<span>
												Step {i + 1} · {tc.toolName}
											</span>
											{state === "error" ? (
												<span className="font-sans font-medium text-destructive">
													Failed
												</span>
											) : null}
										</span>
									}
									description={preview ?? undefined}
									status={
										state === "running"
											? "active"
											: state === "error"
												? "error"
												: "complete"
									}
								>
									<Task defaultOpen={false} className="mt-1">
										<TaskTrigger title={`Inspect ${tc.toolName} call`} />
										<TaskContent>
											<Tool defaultOpen={false}>
												<ToolHeader title={tc.toolName} state={state} />
												<ToolContent>
													<ToolInput input={tc.input ?? tc.args} />
													<ToolOutput
														output={result}
														errorText={errorText ?? undefined}
													/>
												</ToolContent>
											</Tool>
											{files.length > 0 ? (
												<TaskItem className="mt-2 flex flex-wrap gap-1">
													<span className="text-xs text-muted-foreground">
														Files:
													</span>
													{files.map((f) => (
														<TaskItemFile key={f}>{f}</TaskItemFile>
													))}
												</TaskItem>
											) : null}
										</TaskContent>
									</Task>
								</ChainOfThoughtStep>
							);
						})
					: null}
			</ChainOfThoughtContent>
		</ChainOfThought>
	) : null;

	if (chainOnly) return chainEl;

	return (
		<>
			{chainEl}
			{afterSteps}
			{plan ? (
				<Plan
					defaultOpen={true}
					isStreaming={streaming}
					className="my-3 border bg-card/40"
				>
					<PlanHeader>
						<div className="flex flex-1 flex-col gap-1">
							<PlanTitle>{plan.title ?? "Plan"}</PlanTitle>
							{plan.intro ? (
								<PlanDescription>{stripMarkdown(plan.intro)}</PlanDescription>
							) : null}
						</div>
						<PlanAction>
							<PlanTrigger />
						</PlanAction>
					</PlanHeader>
					<PlanContent>
						<ol className="list-decimal space-y-2 pl-5 text-sm">
							{plan.steps.map((s, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: plan steps are positional within the structured payload
								<li key={`step-${i}`}>
									<div className="font-medium">{stripMarkdown(s.title)}</div>
									{s.body ? (
										<div className="text-xs text-muted-foreground">
											{stripMarkdown(s.body)}
										</div>
									) : null}
								</li>
							))}
						</ol>
					</PlanContent>
				</Plan>
			) : null}
			{interaction
				? (() => {
						const resp = interactionResponses?.get(interaction.toolCallId);
						// Resolved → record; active → docked; older turn → collapsed card.
						if (resp) {
							return (
								<ResolvedInteractionCard
									interaction={interaction}
									answers={resp}
								/>
							);
						}
						if (isLatestTurn) return null;
						return (
							<AskQuestionCard
								payload={interaction}
								isActive={false}
								onSubmit={onAnswer}
								disabled={answerInFlight}
							/>
						);
					})()
				: null}
			{scheduledMeeting ? (
				<ScheduledMeetingCard payload={scheduledMeeting.payload} />
			) : null}
			{eoirCheck ? <EoirCheckCard payload={eoirCheck.payload} /> : null}
			{showShimmer ? (
				<Shimmer duration={1.4}>Thinking…</Shimmer>
			) : text.length > 0 ? (
				<MessageResponse>{text}</MessageResponse>
			) : null}
			{sources.length > 0 ? (
				<div className="mt-3 flex items-center gap-1">
					<InlineCitationText className="text-xs text-muted-foreground">
						Sources
					</InlineCitationText>
					{sources.map((s, i) => (
						<InlineCitation key={s.url}>
							<InlineCitationCard>
								<InlineCitationCardTrigger sources={[s.url]}>
									{i + 1}
								</InlineCitationCardTrigger>
								<InlineCitationCardBody>
									<InlineCitationCarousel>
										<InlineCitationCarouselHeader>
											<InlineCitationCarouselPrev />
											<InlineCitationCarouselIndex />
											<InlineCitationCarouselNext />
										</InlineCitationCarouselHeader>
										<InlineCitationCarouselContent>
											<InlineCitationCarouselItem>
												<InlineCitationSource
													title={s.title}
													url={s.url}
													description={s.description}
												/>
											</InlineCitationCarouselItem>
										</InlineCitationCarouselContent>
									</InlineCitationCarousel>
								</InlineCitationCardBody>
							</InlineCitationCard>
						</InlineCitation>
					))}
				</div>
			) : null}
			{sources.length > 0 ? (
				<Sources className="mt-2">
					<SourcesTrigger count={sources.length} />
					<SourcesContent>
						{sources.map((s) => (
							<Source key={s.url} href={s.url} title={s.title ?? s.url} />
						))}
					</SourcesContent>
				</Sources>
			) : null}
		</>
	);
}

type TurnViewProps = {
	turn: TurnGroupT;
	toolResults: Map<string, unknown>;
	role: "user" | "assistant";
	isLatestTurn: boolean;
	activeToolName?: string | null;
	timeLabel?: string;
	turnTime?: number;
	onAnswer?: (answerMessage: string) => void;
	answerInFlight?: boolean;
	interactionResponses?: Map<string, InteractionAnswer[]>;
	cardOutcome?: RunOutcome;
	cardStatusText?: string;
	cardOnRetry?: () => void;
	afterSteps?: React.ReactNode;
};

function UserMessageText({ text }: { text: string }) {
	const segments = splitMentionTokens(text);
	if (!segments.some((segment) => segment.type !== "text")) {
		return <MessageResponse>{text}</MessageResponse>;
	}

	let segmentOffset = 0;
	return (
		<div className="whitespace-pre-wrap break-words">
			{segments.map((segment) => {
				const key = `${segment.type}:${segmentOffset}`;
				segmentOffset += segment.value.length + 1;
				if (segment.type === "text") {
					return <Fragment key={key}>{segment.value}</Fragment>;
				}
				const Icon = segment.type === "workflow" ? WorkflowIcon : SparklesIcon;
				return (
					<span
						key={key}
						data-reference-kind={segment.type}
						className="inline-flex max-w-full items-center gap-1 rounded-md border border-primary/20 bg-primary/10 px-1.5 align-middle text-xs font-medium text-primary"
					>
						<Icon className="size-3.5 shrink-0" />
						<span className="truncate">{segment.value}</span>
					</span>
				);
			})}
		</div>
	);
}

/**
 * Skip re-render when a turn's content + depended-on tool results are unchanged.
 * `turn`/`toolResults` refs flip every Convex update, but nested `ContentPart`
 * and tool-result values stay reference-stable — compare those instead.
 */
function areTurnPropsEqual(prev: TurnViewProps, next: TurnViewProps): boolean {
	if (
		prev.role !== next.role ||
		prev.isLatestTurn !== next.isLatestTurn ||
		prev.activeToolName !== next.activeToolName ||
		prev.timeLabel !== next.timeLabel ||
		prev.turnTime !== next.turnTime ||
		prev.onAnswer !== next.onAnswer ||
		prev.answerInFlight !== next.answerInFlight ||
		prev.cardOutcome !== next.cardOutcome ||
		prev.cardStatusText !== next.cardStatusText ||
		prev.cardOnRetry !== next.cardOnRetry ||
		prev.afterSteps !== next.afterSteps
	) {
		return false;
	}
	const a = prev.turn;
	const b = next.turn;
	if (a !== b) {
		if (
			a.key !== b.key ||
			a.streaming !== b.streaming ||
			a.failed !== b.failed ||
			a.errorMessage !== b.errorMessage ||
			a.text !== b.text ||
			a.parts.length !== b.parts.length ||
			a.pending !== b.pending
		) {
			return false;
		}
		for (let i = 0; i < a.parts.length; i++) {
			if (a.parts[i] !== b.parts[i]) return false;
		}
	}
	if (prev.toolResults !== next.toolResults) {
		for (const part of next.turn.parts) {
			if (part.type !== "tool-call") continue;
			const id = (part as { toolCallId: string }).toolCallId;
			if (prev.toolResults.get(id) !== next.toolResults.get(id)) return false;
		}
	}
	if (prev.interactionResponses !== next.interactionResponses) {
		for (const part of next.turn.parts) {
			if (part.type !== "tool-call") continue;
			const id = (part as { toolCallId: string }).toolCallId;
			if (
				prev.interactionResponses?.get(id) !==
				next.interactionResponses?.get(id)
			) {
				return false;
			}
		}
	}
	return true;
}

const TurnView = memo(function TurnView({
	turn,
	toolResults,
	role,
	isLatestTurn,
	activeToolName,
	timeLabel,
	turnTime,
	onAnswer,
	answerInFlight,
	interactionResponses,
	cardOutcome,
	cardStatusText,
	cardOnRetry,
	afterSteps,
}: TurnViewProps) {
	return (
		<>
			<Message
				from={role}
				className="max-w-3xl"
				data-testid={`${role}-message`}
				data-streaming={turn.streaming ? "true" : "false"}
			>
				<MessageContent>
					{role === "assistant" ? (
						<AssistantBody
							parts={turn.parts}
							text={turn.text}
							toolResults={toolResults}
							streaming={turn.streaming}
							pending={turn.pending}
							isLatestTurn={isLatestTurn}
							activeToolName={activeToolName}
							onAnswer={onAnswer}
							answerInFlight={answerInFlight}
							interactionResponses={interactionResponses}
							afterSteps={afterSteps}
						/>
					) : (
						<UserMessageText text={turn.text} />
					)}
					{timeLabel ? (
						<time
							dateTime={turnTime ? new Date(turnTime).toISOString() : undefined}
							className={
								role === "user"
									? "mt-1 block text-right text-[10px] text-muted-foreground"
									: "mt-1 block text-[10px] text-muted-foreground/70"
							}
						>
							{timeLabel}
						</time>
					) : null}
				</MessageContent>
			</Message>
			{cardOutcome ? (
				<RunOutcomeCard
					outcome={cardOutcome}
					statusText={cardStatusText}
					onRetry={cardOnRetry}
				/>
			) : null}
		</>
	);
}, areTurnPropsEqual);

// main component

// Lets browser skip style/layout/paint for off-screen turns; hoisted to avoid per-render allocation.
const itemVisibilityStyle: CSSProperties = {
	contentVisibility: "auto",
	containIntrinsicSize: "auto 400px",
};

export default function ChatMessages({
	messages: rawMessages,
	status,
	loadMore,
	onAnswerQuestions,
	answerInFlight,
	activeToolName,
	outcome,
	outcomeStatusText,
	onRetryOutcome,
	footer,
	renderAfterSteps,
	focusMessageId,
	toolResponses,
	hiddenMessageIds,
	contextCompaction,
	turnContinuations,
}: {
	clericalThreadId: string;
	messages: ReadonlyArray<unknown>;
	/** Structured answers to interactive tool calls on this thread. */
	toolResponses?: ReadonlyArray<{
		toolCallId: string;
		toolName: string;
		answers: InteractionAnswer[];
	}>;
	/** Transcript message ids to suppress (superseded by richer UI); matched by id only. */
	hiddenMessageIds?: ReadonlySet<string>;
	contextCompaction?: ContextCompactionUiState;
	turnContinuations?: TurnContinuationUiState;
	status: "LoadingFirstPage" | "CanLoadMore" | "LoadingMore" | "Exhausted";
	loadMore: (numItems: number) => void;
	onAnswerQuestions?: (answerMessage: string) => void;
	answerInFlight?: boolean;
	activeToolName?: string | null;
	outcome?: RunOutcome;
	outcomeStatusText?: string;
	onRetryOutcome?: () => void;
	/** Search palette `?focus=` deep-link: scroll to + highlight this message, paging in older history. */
	focusMessageId?: string;
	/** Rendered after the turns, inside the scroll area (e.g. workflow step activity). */
	footer?: React.ReactNode;
	/** Render content after activity steps and before assistant prose. */
	renderAfterSteps?: (turn: TurnGroupT, index: number) => React.ReactNode;
}) {
	const messages = rawMessages as unknown as ChatMessage[];

	const toolResults = useMemo(
		() =>
			buildToolResultMap(
				messages.map((m) => ({ message: m.message })) as Array<{
					message?: { role: string; content: unknown };
				}>,
			),
		[messages],
	);

	const turns = useMemo(
		() => groupMessagesByTurn(messages).filter((t) => t.role !== "system"),
		[messages],
	);
	const compactionMarkers = contextCompactionMarkers(
		turns.map((turn) => turn.startedAt),
		contextCompaction,
	);
	const continuationMarkers = turnContinuationMarkers(
		turns.map((turn) => turn.startedAt),
		turnContinuations,
	);
	const renderCompactionMarkers = (index: number) =>
		compactionMarkers
			.filter((marker) => marker.index === index)
			.map((marker) => (
				<ContextCompactionMarker key={marker.key} status={marker.status} />
			));
	const renderContinuationMarkers = (index: number) =>
		continuationMarkers
			.filter((marker) => marker.index === index)
			.map((marker) => (
				<TurnContinuationMarker key={marker.key} reason={marker.reason} />
			));

	// Structured answers keyed by the toolCallId they answer.
	const responsesByCallId = useMemo(() => {
		const m = new Map<string, InteractionAnswer[]>();
		for (const r of toolResponses ?? []) m.set(r.toolCallId, r.answers);
		return m;
	}, [toolResponses]);

	// Which turns to suppress — resolved by id/structure, never by message text.
	const hiddenAnswerTurnKeys = useMemo(() => {
		const hidden = new Set<string>();
		// (1) Messages the workflow tagged superseded (gate announcement + decision notes), by id.
		if (hiddenMessageIds && hiddenMessageIds.size > 0) {
			for (const t of turns) {
				if (t.messages.some((m) => hiddenMessageIds.has(m._id))) {
					hidden.add(t.key);
				}
			}
		}
		// (2) Hide each registered interaction's structured answer turn.
		if (responsesByCallId.size > 0) {
			for (let i = 0; i < turns.length - 1; i++) {
				const t = turns[i];
				if (t.role !== "assistant") continue;
				const interaction = interactionPartFor(t.parts);
				if (!interaction || !responsesByCallId.has(interaction.toolCallId)) {
					continue;
				}
				const next = turns[i + 1];
				if (next.role === "user") hidden.add(next.key);
			}
		}
		return hidden;
	}, [turns, responsesByCallId, hiddenMessageIds]);

	const latestFailedTurnIdx = useMemo(() => {
		for (let i = turns.length - 1; i >= 0; i--) {
			if (turns[i].failed) return i;
		}
		return -1;
	}, [turns]);

	// Cancel patches the thread row instantly, but the agent's `status: "failed"` write
	// lands a tick later — dangle the outcome card on the last turn until then.
	const dangleOutcomeAtEnd =
		outcome !== undefined && latestFailedTurnIdx === -1;

	const isInitialLoading = status === "LoadingFirstPage";
	const isLoadingMore = status === "LoadingMore";
	const canLoadMore = status === "CanLoadMore";

	const handleLoadMore = useCallback(() => loadMore(50), [loadMore]);

	// Callback ref: IntersectionObserver on a 1px top sentinel triggers loadMore within 400px.
	const topSentinelRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (!node || !canLoadMore) return;
			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting) handleLoadMore();
				},
				{ rootMargin: "400px 0px 0px 0px", threshold: 0 },
			);
			observer.observe(node);
			return () => observer.disconnect();
		},
		[canLoadMore, handleLoadMore],
	);

	// Deep-link target from `?focus=`; paged into view below.
	const lastScrolledRef = useRef<string | null>(null);
	const activeTargetId = focusMessageId;

	// Message `_id` → turn key, same join ChatPage's `anchorByMessageId` uses; resolves focus target to a DOM node.
	const messageIdToTurnKey = useMemo(() => {
		const map = new Map<string, string>();
		for (const turn of turns) {
			for (const m of turn.messages) map.set(m._id, turn.key);
		}
		return map;
	}, [turns]);
	const activeTurnKey = activeTargetId
		? messageIdToTurnKey.get(activeTargetId)
		: undefined;

	// Scrolls focus target into view, paging older messages until found or `Exhausted`.
	// Callback-ref, no useEffect — mirrors `topSentinelRef`.
	const scrollDriverRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (!node || !activeTargetId) return;
			const turnKey = messageIdToTurnKey.get(activeTargetId);
			if (turnKey) {
				if (lastScrolledRef.current !== activeTargetId) {
					lastScrolledRef.current = activeTargetId;
					document
						.getElementById(`chat-turn-${turnKey}`)
						?.scrollIntoView({ behavior: "smooth", block: "center" });
				}
				return;
			}
			if (status === "CanLoadMore") loadMore(50);
		},
		[activeTargetId, messageIdToTurnKey, status, loadMore],
	);

	const lastIdx = turns.length - 1;

	// `initial={false}` when deep-linking suppresses auto-scroll so focus `scrollIntoView` wins.
	return (
		<Conversation
			initial={focusMessageId ? false : "smooth"}
			className="h-full min-h-0 flex-1"
		>
			<ConversationContent className="mx-auto w-full max-w-5xl">
				{/* Hidden driver: scrolls deep-link focus target into view, paging in history. */}
				<div ref={scrollDriverRef} aria-hidden className="hidden" />
				{canLoadMore || isLoadingMore ? (
					<div ref={topSentinelRef} aria-hidden className="h-px" />
				) : null}
				{isInitialLoading || isLoadingMore ? (
					<div className="flex flex-col gap-6 px-4 py-4">
						<MessageSkeleton />
						{isInitialLoading ? <MessageSkeleton /> : null}
					</div>
				) : null}
				{!isInitialLoading && turns.length === 0 ? (
					<ConversationEmptyState
						title="Conversation is empty"
						description="Send a message below to begin."
					/>
				) : null}
				{turns.map((turn, idx) => {
					const compactionMarker = renderCompactionMarkers(idx);
					const continuationMarker = renderContinuationMarkers(idx);
					// Suppressed turns may still anchor checkpoint content.
					if (hiddenAnswerTurnKeys.has(turn.key)) {
						const afterHidden = renderAfterSteps?.(turn, idx);
						return continuationMarker || compactionMarker || afterHidden ? (
							<Fragment key={turn.key}>
								{continuationMarker}
								{compactionMarker}
								{afterHidden}
							</Fragment>
						) : null;
					}
					const role: "user" | "assistant" =
						turn.role === "assistant" ? "assistant" : "user";
					const isLatestTurn = idx === lastIdx;
					const isLatestFailed = idx === latestFailedTurnIdx;
					const anchorOutcome =
						isLatestFailed || (isLatestTurn && dangleOutcomeAtEnd);
					const showCardAfter =
						turn.failed || (isLatestTurn && dangleOutcomeAtEnd);
					const cardOutcome: RunOutcome | undefined = showCardAfter
						? anchorOutcome
							? (outcome ?? runOutcomeFromError(turn.errorMessage))
							: runOutcomeFromError(turn.errorMessage)
						: undefined;
					const cardStatusText = anchorOutcome ? outcomeStatusText : undefined;
					const cardOnRetry = anchorOutcome ? onRetryOutcome : undefined;
					const turnTime = turn.startedAt;
					const timeLabel = turnTime ? formatMessageTime(turnTime) : undefined;
					const afterSteps = renderAfterSteps?.(turn, idx);
					const isFocusTarget = turn.key === activeTurnKey;
					return (
						<Fragment key={turn.key}>
							{continuationMarker}
							{compactionMarker}
							<div
								id={`chat-turn-${turn.key}`}
								style={itemVisibilityStyle}
								className={cn(
									// content-visibility paint-clips at the rounded corners; p-2/-m-2 keeps text off the arc
									"-m-2 scroll-mt-24 rounded-lg p-2 transition-shadow",
									isFocusTarget
										? "ring-2 ring-primary ring-offset-2 ring-offset-background"
										: undefined,
								)}
							>
								<TurnView
									turn={turn}
									toolResults={toolResults}
									role={role}
									isLatestTurn={isLatestTurn}
									activeToolName={isLatestTurn ? activeToolName : undefined}
									timeLabel={timeLabel}
									turnTime={turnTime}
									onAnswer={onAnswerQuestions}
									answerInFlight={answerInFlight}
									interactionResponses={responsesByCallId}
									cardOutcome={cardOutcome}
									cardStatusText={cardStatusText}
									cardOnRetry={cardOnRetry}
									afterSteps={afterSteps}
								/>
							</div>
						</Fragment>
					);
				})}
				{renderContinuationMarkers(turns.length)}
				{renderCompactionMarkers(turns.length)}
				{footer}
			</ConversationContent>
			<ConversationScrollButton />
		</Conversation>
	);
}
